import React, { useId, useState, useRef, useEffect } from 'react';

import { useInView } from "react-intersection-observer";

import { graphql, useStaticQuery } from 'gatsby';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';

import { motion } from 'framer-motion';

import Typography from '@mui/material/Typography';

import { fonts } from '../gatsby-theme-material-ui-top-layout/theme';

import styled from 'styled-components';

import IconButton from '@mui/material/IconButton';
import Box from '@mui/material/Box';

import ArrowHorizontal from '../img/arrow-horizontal.svg';

import {
  PROPY_SPACE_GREY_LIGHT,
  PROPY_BLACK,
} from '../utils';

const ZoneContainer = styled.div`
  width: 100%;
  overflow: hidden;
  display: flex;
  justify-content: center;
  align-items: center;
`

const Zone = styled.div`
  max-width: 1080px;
  width: 100%;
`

const TypographyWithControlsContainer = styled.div`
  margin-bottom: ${props => props.mobile ? props.theme.spacing(4) : props.theme.spacing(8)};
  display: flex;
  justify-content: space-between;
`

const TypographyContainer = styled.div`
  max-width: ${props => props.mobile ? 'auto' : '1060px'};
  margin-left: ${props => props.mobile ? props.theme.spacing(2) : '0px'};
  margin-right: ${props => props.mobile ? props.theme.spacing(2) : '0px'};
`

const Controls = styled.div`
  display: flex;
  align-items: end;
`

const TypographySpacer = styled.div`
  margin-top: ${props => props.theme.spacing(1.5)};
`

const ImageRowContainer = styled(motion.div)`
  display: flex;
  position: relative;
  overflow-x: visible;
  padding-left: ${props => props.mobile ? props.theme.spacing(2) : '0px'};
  padding-right: ${props => props.mobile ? props.theme.spacing(2) : '0px'};
  padding-bottom: ${props => props.mobile ? props.theme.spacing(2) : '0px'};
`

const ImageEntry = styled(motion.div)`
  width: 200px;
  height: 200px;
  display: inline-block;
  margin-right: ${props => props.theme.spacing(2)};
  border-radius: 8px;
  flex-shrink: 0;
  overflow: hidden;
`

const MobileControls = styled.div`
  margin-top: ${props => props.theme.spacing(1.5)};
  display: flex;
  justify-content: center;
`

const ScrollTrack = styled.div`
  display: flex;
  width: fit-content;
  transform: translate3d(${props => {
    return (props.totalOffset > props.maxTotalOffset) ? props.totalOffset : props.maxTotalOffset
  }}px, 0, 0);
  transition: transform 0.1s linear;
  will-change: transform;
`;

const SINGLE_ENTRY_WIDTH = 200;
const SINGLE_ENTRY_MARGIN_RIGHT = 16;
const COMBINED_SINGLE_ENTRY_WIDTH = SINGLE_ENTRY_WIDTH + SINGLE_ENTRY_MARGIN_RIGHT;

const PioneerGallery = ({
  isConsideredMobile,
}) => {

  const data = useStaticQuery(graphql`
    query {
      allFile(
        filter: {
          sourceInstanceName: { eq: "images" }
          relativePath: { regex: "/^pioneer-\\d+/" }
        },
        sort: { 
          fields: relativePath
          order: ASC
        }
      ) {
        edges {
          node {
            relativePath
            childImageSharp {
              gatsbyImageData(
                width: 200
                height: 200
                placeholder: BLURRED
                layout: FIXED
                quality: 100
              )
            }
          }
        }
      }
    }
  `);

  const images = data.allFile.edges.map(edge => getImage(edge.node));
  
  const zoneRef = useRef(null);
  const [imagesPerView, setImagesPerView] = useState(5); // default value
  const [currentIndex, setCurrentIndex] = useState(0);
  const [absolutePositionFromTop, setAbsolutePositionFromTop] = useState(0);
  const [scrollOffset, setScrollOffset] = useState(0);
  const [buttonOffset, setButtonOffset] = useState(0);
  const [maxTotalOffset, setMaxTotalOffset] = useState(0);
  const [hasUsedButton, setHasUsedButton] = useState(false);
  const totalOffset = buttonOffset + scrollOffset;
  const galleryId = useId();

  const { ref, inView, entry } = useInView({
    threshold: 0,
  });

  useEffect(() => {
    if(inView) {
      setAbsolutePositionFromTop(window.scrollY + entry.boundingClientRect.top);
    }
  }, [inView, entry]);

  useEffect(() => {
    const handleScroll = () => {
      if (!inView || hasUsedButton) return;
      
      // Calculate how far we've scrolled past the component
      const scrollPastComponent = (window.scrollY + window.innerHeight) - absolutePositionFromTop;
      
      // Only start translating after we've scrolled past the component
      if (scrollPastComponent > 0) {
        const newBaseOffset = -scrollPastComponent * (isConsideredMobile ? 0.25 : 0.5);
        
        const combinedOffset = newBaseOffset;
        if (
          combinedOffset <= 0
        ) {
          setScrollOffset(combinedOffset);
        }
      } else {
        setScrollOffset(0);
      }
    };
  
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [inView, entry, absolutePositionFromTop, isConsideredMobile, hasUsedButton]);

  // Calculate visible images on mount and resize
  useEffect(() => {
    const calculateVisibleImages = () => {
      if (!zoneRef.current) return;
      
      const zoneWidth = zoneRef.current.offsetWidth;
      const imageWidth = SINGLE_ENTRY_WIDTH;
      const marginRight = SINGLE_ENTRY_MARGIN_RIGHT;
      const totalImageWidth = imageWidth + marginRight;
      
      // Calculate how many whole images fit
      const visibleImages = Math.floor(zoneWidth / totalImageWidth);
      const maxTotalOffset = ((COMBINED_SINGLE_ENTRY_WIDTH * images.length) - (COMBINED_SINGLE_ENTRY_WIDTH * visibleImages)) * -1;
      setImagesPerView(visibleImages);
      setMaxTotalOffset(maxTotalOffset);
    };

    calculateVisibleImages();
    
    // Add resize listener
    window.addEventListener('resize', calculateVisibleImages);
    return () => window.removeEventListener('resize', calculateVisibleImages);
  }, [images]);

  const isAtEnd = () => {
    return currentIndex + imagesPerView >= images.length;
  };

  const handleNext = () => {
    if (!isAtEnd()) {
      const remainingImages = images.length - currentIndex - imagesPerView;
      const scrollCount = remainingImages < imagesPerView ? remainingImages : imagesPerView;
      let newButtonOffset = (currentIndex + scrollCount) * COMBINED_SINGLE_ENTRY_WIDTH * -1;
      setHasUsedButton(true);
      setButtonOffset(newButtonOffset);
      setCurrentIndex(prev => prev + scrollCount);
    }
  };

  const handlePrev = () => {
    if (currentIndex > 0) {
      const scrollCount = Math.min(imagesPerView, currentIndex);
      let newButtonOffset = (currentIndex - scrollCount) * (COMBINED_SINGLE_ENTRY_WIDTH) * -1;
      setHasUsedButton(true);
      if((currentIndex - scrollCount) > 0) {
        setButtonOffset(newButtonOffset);
        setCurrentIndex(prev => prev - scrollCount);
      } else {
        setButtonOffset(0);
        setCurrentIndex(0);
        setScrollOffset(0);
      }
    }
  };

  return (
    <ZoneContainer>
      <Zone ref={zoneRef}>
        <TypographyContainer mobile={isConsideredMobile}>
           <Typography sx={{ fontFamily: fonts.spaceGrotesk, color: PROPY_SPACE_GREY_LIGHT, letterSpacing: '0.12em', textAlign: isConsideredMobile ? 'center' : 'left', ...(isConsideredMobile && { fontSize: '14px', lineHeight: '14px' }) }}>OWN YOUR TOMORROW</Typography>
           <TypographySpacer>
             <TypographyWithControlsContainer mobile={isConsideredMobile}>
                <Typography variant="h4" sx={{ fontFamily: fonts.spaceGrotesk, color: PROPY_BLACK, letterSpacing: '-0.01em', textAlign: isConsideredMobile ? 'center' : 'left', ...(isConsideredMobile && { fontSize: '24px', lineHeight: '24px' }) }}>Propy is a licensed Web3 pioneer, operating since 2017, facilitating over $4 billion in transactions.</Typography>
                {!isConsideredMobile &&
                  <Controls>
                    <IconButton
                      onClick={handlePrev}
                      disabled={currentIndex === 0 && !(totalOffset < -20)}
                      sx={{
                        border: '1px solid #38A6FB',
                        height: '44px',
                        width: '44px',
                        marginRight: '8px',
                        opacity: (currentIndex === 0 && !(totalOffset < -20)) ? 0.5 : 1,
                      }} 
                      aria-label="backwards"
                    >
                      <Box
                        component={ArrowHorizontal}
                        sx={{
                          width: '24px',
                          height: '24px',
                          color: 'primary.main',
                          '& path': { fill: '#38A6FB' }
                        }}
                      />
                    </IconButton>
                    <IconButton
                      onClick={handleNext}
                      disabled={isAtEnd()} // Updated condition here
                      sx={{
                        border: '1px solid #38A6FB',
                        height: '44px',
                        width: '44px',
                        opacity: isAtEnd() ? 0.5 : 1, // And here
                      }}
                      aria-label="forwards"
                    >
                      <Box
                        component={ArrowHorizontal}
                        sx={{
                          width: 24,
                          height: 24,
                          transform: 'rotate(180deg)',
                          color: 'primary.main',
                          '& path': { fill: '#38A6FB' }
                        }}
                      />
                    </IconButton>
                  </Controls>
                }
            </TypographyWithControlsContainer>
          </TypographySpacer>
        </TypographyContainer>
        <ImageRowContainer mobile={isConsideredMobile}>
          <ScrollTrack totalOffset={totalOffset} maxTotalOffset={maxTotalOffset} mobile={isConsideredMobile}>
            {images.map((image, index) => (
              <ImageEntry
                key={`${galleryId}-${index}`}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
              >
                <GatsbyImage
                  image={image}
                  alt={`pioneer ${index}`}
                />
              </ImageEntry>
            ))}
          </ScrollTrack>
        </ImageRowContainer>
        <div ref={ref} />
        {isConsideredMobile &&
          <MobileControls>
            <Controls>
              <IconButton
                onClick={handlePrev}
                disabled={currentIndex === 0 && !(totalOffset < -20)}
                sx={{
                  border: '1px solid #38A6FB',
                  height: '44px',
                  width: '44px',
                  marginRight: '8px',
                  opacity: (currentIndex === 0 && !(totalOffset < -20)) ? 0.5 : 1,
                }} 
                aria-label="backwards"
              >
                <Box
                  component={ArrowHorizontal}
                  sx={{
                    width: '24px',
                    height: '24px',
                    color: 'primary.main',
                    '& path': { fill: '#38A6FB' }
                  }}
                />
              </IconButton>
              <IconButton
                onClick={handleNext}
                disabled={isAtEnd()} // Updated condition here
                sx={{
                  border: '1px solid #38A6FB',
                  height: '44px',
                  width: '44px',
                  opacity: isAtEnd() ? 0.5 : 1, // And here
                }}
                aria-label="forwards"
              >
                <Box
                  component={ArrowHorizontal}
                  sx={{
                    width: 24,
                    height: 24,
                    transform: 'rotate(180deg)',
                    color: 'primary.main',
                    '& path': { fill: '#38A6FB' }
                  }}
                />
              </IconButton>
            </Controls>
          </MobileControls>
        }
      </Zone>
    </ZoneContainer>
  );
};

export default PioneerGallery;